

import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Form,
  Row,
  Col,
  Card,
  Alert,
  Pagination,
  InputGroup,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Modal,
  Toast,
  ToastContainer
} from "react-bootstrap";
import * as XLSX from "xlsx";
import axios from "axios";
import Menubar from "./Menubar";
import BackgroundFooter from "./BackgroundFooter";
import "../ComponentsCss/AssetInventory.css";
import NavigationBar from "./Nav";
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from "../contexts/AuthContext";
import { logActivity } from '../utils/activityLogApi';


const AssetInventory = ({ userData }) => {
  const [assets, setAssets] = useState([]);
  const [newAsset, setNewAsset] = useState({
    assetSerialNumber: "",           // ✅ NEW
    assetCategory: "",               // ✅ NEW
    assetName: "",
    description: "",
    location: "",
    owner: "",
    assetValue: "",
    costType: "",           // NEW
    classification: "",     // NEW
    exposureFactor: 50,
    annualizedRateOfOccurrence: 0.2
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() =>
    JSON.parse(localStorage.getItem("isSidebarCollapsed")) || false
  );

  const region = sessionStorage.getItem("userRegion") || "GB";
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const { profile } = useAuth();
  const orgId = profile?.orgId;
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const UPDATE_ASSET_URL = "https://qjy3r5moib.execute-api.ap-southeast-2.amazonaws.com/prod/api/assets/update";
  const [showEditModal, setShowEditModal] = useState(false);
const [editAsset, setEditAsset] = useState({
  assetId: "",
  assetSerialNumber: "",
  assetCategory: "",
  assetName: "",
  description: "",
  location: "",
  owner: "",
  assetValue: "",
  costType: "",
  classification: "",
  exposureFactor: 50,
  annualizedRateOfOccurrence: 0.2
});
  const resetForm = () => {
  setNewAsset({
    assetSerialNumber: "",
    assetCategory: "",
    assetName: "",
    description: "",
    location: "",
    owner: "",
    assetValue: "",
    costType: "",
    classification: "",
    exposureFactor: 50,
    annualizedRateOfOccurrence: 0.2
  });
  setIsEditMode(false);
  setEditingAssetId(null);
  setShowAlert(false);
};

const handleDuplicateAsset = async (asset) => {
  const duplicatedAsset = {
    ...asset,
    assetId: uuidv4(),
    assetName: asset.assetName ? `${asset.assetName} Copy` : "Copied Asset",
    orgId,
    currency: "USD",
    assetValue: asset.assetValue === "" || asset.assetValue == null ? null : Number(asset.assetValue),
    exposureFactor: Number(asset.exposureFactor ?? 50),
    annualizedRateOfOccurrence: Number(asset.annualizedRateOfOccurrence ?? 0.2),
    isSaved: true
  };

  try {
    await axios.post(
       "https://oxu04tbck9.execute-api.ap-southeast-2.amazonaws.com/prod/api/assets/save",
      [duplicatedAsset],
      { headers: { Region: region, "Content-Type": "application/json" } }
    );

    setAssets((prev) => [...prev, duplicatedAsset]);
    setToastMessage(`✅ Asset "${asset.assetName}" duplicated successfully!`);
     logActivity({
                  userData,
                  orgId:       orgId,
                  region:      sessionStorage.getItem('userRegion') || 'AP',
                  module:      'Asset Inventory',
                  category:    'assets',
                  action:      'Assets duplication',
                  severity:    'success',
                  description: `Asset "${asset.assetName}" is duplicated`,});
    setToastType("success");
    setShowToast(true);
  } catch (err) {
    setToastMessage("❌ Failed to duplicate asset.");
    setToastType("error");
    setShowToast(true);
  }
};

const handleEditAsset = (asset) => {
  setEditAsset({
    assetId: asset.assetId || "",
    assetSerialNumber: asset.assetSerialNumber || "",
    assetCategory: asset.assetCategory || "",
    assetName: asset.assetName || "",
    description: asset.description || "",
    location: asset.location || "",
    owner: asset.owner || "",
    assetValue: asset.assetValue ?? "",
    costType: asset.costType || "",
    classification: asset.classification || "",
    exposureFactor: asset.exposureFactor ?? 50,
    annualizedRateOfOccurrence: asset.annualizedRateOfOccurrence ?? 0.2
  });
  setShowEditModal(true);
};

const handleEditChange = (e) => {
  setEditAsset({ ...editAsset, [e.target.name]: e.target.value });
};

const handleUpdateAsset = async () => {
  if (!editAsset.assetName || !editAsset.location || !editAsset.owner) {
    setToastMessage("⚠️ Name, Location, and Owner are required.");
    setToastType("error");
    setShowToast(true);
    return;
  }

  if (editAsset.assetValue !== "" && (isNaN(Number(editAsset.assetValue)) || Number(editAsset.assetValue) < 0)) {
    setToastMessage("⚠️ Asset Value must be a valid positive number.");
    setToastType("error");
    setShowToast(true);
    return;
  }

  if (isNaN(Number(editAsset.exposureFactor)) || Number(editAsset.exposureFactor) < 0 || Number(editAsset.exposureFactor) > 100) {
    setToastMessage("⚠️ Exposure Factor must be between 0 and 100.");
    setToastType("error");
    setShowToast(true);
    return;
  }

  if (isNaN(Number(editAsset.annualizedRateOfOccurrence)) || Number(editAsset.annualizedRateOfOccurrence) < 0) {
    setToastMessage("⚠️ ARO must be a valid positive number.");
    setToastType("error");
    setShowToast(true);
    return;
  }

  const payload = {
    ...editAsset,
    orgId,
    currency: "USD",
    assetValue: editAsset.assetValue === "" ? null : Number(editAsset.assetValue),
    exposureFactor: Number(editAsset.exposureFactor),
    annualizedRateOfOccurrence: Number(editAsset.annualizedRateOfOccurrence),
    isSaved: true
  };

  try {
    setLoading(true);

    await axios.put(
      UPDATE_ASSET_URL,
      payload,
      { headers: { Region: region, "Content-Type": "application/json" } }
    );

    setAssets((prev) =>
      prev.map((asset) =>
        asset.assetId === editAsset.assetId ? { ...asset, ...payload } : asset
      )
    );

    setToastMessage(`✅ Asset "${payload.assetName}" updated successfully!`);
      logActivity({
                  userData,
                  orgId:       orgId,
                  region:      sessionStorage.getItem('userRegion') || 'AP',
                  module:      'Asset Inventory',
                  category:    'assets',
                  action:      'Asset saved',
                  severity:    'success',
                  description: `Asset "${payload.assetName}" is saved`,});
    setToastType("success");
    setShowToast(true);
    setShowEditModal(false);
  } catch (err) {
    setToastMessage("❌ Failed to update asset. Please try again.");
    setToastType("error");
    setShowToast(true);
  } finally {
    setLoading(false);
  }
};
  const formatCurrency = (v) => {
    if (v === undefined || v === null || v === "") return "";
    const num = Number(v);
    if (Number.isNaN(num)) return v;
    return num.toLocaleString(undefined, { style: "currency", currency: "USD" });
  };

  

  // ── Fetch assets on mount ──
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    axios
      .get(
        "https://hwul63huka.execute-api.ap-southeast-2.amazonaws.com/prod/api/assets/get-by-user",
        { params: { orgId }, headers: { Region: region } }
      )
      .then((res) => setAssets(res.data || []))
      .catch(() => setErrorMessage("❌ Failed to fetch assets."))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    localStorage.setItem("isSidebarCollapsed", JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const handleSidebarToggle = () => setIsSidebarCollapsed((prev) => !prev);

  const handleChange = (e) => {
    setNewAsset({ ...newAsset, [e.target.name]: e.target.value });
  };

  // ── Download sample template ──
  const handleDownloadSample = () => {
    const sampleData = [
      {
        "Asset ID":                      "(auto-generated)",
        "Asset Serial Number":           "SN-2024-001",
        "Asset Category":                "Hardware",
        "Name of the Asset":             "Primary Web Server",
        "Description":                   "Customer-facing Apache web server",
        "Location":                      "London DC",
        "Owner":                         "IT Manager",
        "CAPEX/OPEX":                    "CAPEX",
        "Classification":                 "HIGH",
        "Asset Value":                   150000,
        "Exposure Factor (%)":           60,
        "Annualized Rate of Occurrence": 0.5
      },
      {
        "Asset ID":                      "(auto-generated)",
        "Asset Serial Number":           "SN-2024-002",
        "Asset Category":                "Software",
        "Name of the Asset":             "Customer Database",
        "Description":                   "PostgreSQL database storing PII records",
        "Location":                      "AWS eu-west-1",
        "Owner":                         "DBA Team",
         "CAPEX/OPEX":                    "CAPEX",
        "Classification":                 "LOW",
        "Asset Value":                   500000,
        "Exposure Factor (%)":           80,
        "Annualized Rate of Occurrence": 0.2
      },
      {
        "Asset ID":                      "(auto-generated)",
        "Asset Serial Number":           "SN-2024-003",
        "Asset Category":                "People",
        "Name of the Asset":             "Employee Laptops",
        "Description":                   "Windows 11 laptops issued to all staff",
        "Location":                      "Remote / Office",
        "Owner":                         "HR / IT",
        "CAPEX/OPEX":                    "CAPEX",
        "Classification":                 "LOW",
        "Asset Value":                   80000,
        "Exposure Factor (%)":           40,
        "Annualized Rate of Occurrence": 1
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const colWidths = Object.keys(sampleData[0]).map((key) => ({ wch: Math.max(key.length, 20) }));
    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Asset Inventory");
    XLSX.writeFile(workbook, "Asset_Inventory_Template.xlsx");
  };

  // ── Add single asset ──
const handleSaveAsset = async () => {
  if (!newAsset.assetName || !newAsset.location || !newAsset.owner) {
    setShowAlert(true);
    return;
  }

  if (newAsset.assetValue !== "" && (isNaN(Number(newAsset.assetValue)) || Number(newAsset.assetValue) < 0)) {
    setToastMessage("⚠️ Asset Value must be a valid positive number.");
    setToastType("error");
    setShowToast(true);
    return;
  }

  if (isNaN(Number(newAsset.exposureFactor)) || Number(newAsset.exposureFactor) < 0 || Number(newAsset.exposureFactor) > 100) {
    setToastMessage("⚠️ Exposure Factor must be between 0 and 100.");
    setToastType("error");
    setShowToast(true);
    return;
  }

  if (isNaN(Number(newAsset.annualizedRateOfOccurrence)) || Number(newAsset.annualizedRateOfOccurrence) < 0) {
    setToastMessage("⚠️ ARO must be a valid positive number.");
    setToastType("error");
    setShowToast(true);
    return;
  }

  const currency = "USD";

  const payload = {
    ...newAsset,
    assetId: isEditMode ? editingAssetId : uuidv4(),
    orgId,
    currency,
    assetValue: newAsset.assetValue === "" ? null : Number(newAsset.assetValue),
    exposureFactor: Number(newAsset.exposureFactor),
    annualizedRateOfOccurrence: Number(newAsset.annualizedRateOfOccurrence),
    isSaved: true
  };

  try {
    if (isEditMode) {
      await axios.put(UPDATE_ASSET_URL, payload, { headers: { Region: region, "Content-Type": "application/json" } });

      setAssets((prev) =>
        prev.map((asset) =>
          asset.assetId === editingAssetId ? { ...asset, ...payload } : asset
        )
      );

      setToastMessage(`✅ Asset "${payload.assetName}" updated successfully!`);
    } else {
      await axios.post(
        "https://oxu04tbck9.execute-api.ap-southeast-2.amazonaws.com/prod/api/assets/save",
        [payload],
        { headers: { Region: region, "Content-Type": "application/json" } }
      );

      setAssets((prev) => [...prev, payload]);
      setToastMessage(`✅ Asset "${payload.assetName}" saved successfully!`);
      logActivity({
                  userData,
                  orgId:       orgId,
                  region:      sessionStorage.getItem('userRegion') || 'AP',
                  module:      'Asset Inventory',
                  category:    'assets',
                  action:      'Asset updated',
                  severity:    'success',
                  description: `Asset "${payload.assetName}" is updated`,});
    }

    setToastType("success");
    setShowToast(true);
    setErrorMessage("");
    resetForm();
  } catch (err) {
    setToastMessage(
      isEditMode
        ? "❌ Failed to update asset. Please try again."
        : "❌ Failed to save asset. Please try again."
    );
    setToastType("error");
    setShowToast(true);
  }
};

  // ── Upload from XLSX ──
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      if (rows.length === 0) {
        setToastMessage("⚠️ No data found in the uploaded file.");
        setToastType("error");
        setShowToast(true);
        return;
      }

      const formattedAssets = rows.map((row) => {
        const rawAssetId = row["Asset ID"] || "";
        const isPlaceholder = rawAssetId === "(auto-generated)" || rawAssetId === "";

        return {
          assetId:                    isPlaceholder ? uuidv4() : rawAssetId,
          assetSerialNumber:          row["Asset Serial Number"] || "",   // ✅ NEW
          assetCategory:              row["Asset Category"] || "",         // ✅ NEW
          assetName:                  row["Name of the Asset"] || "",
          description:                row["Description"] || "",
          location:                   row["Location"] || "",
          owner:                      row["Owner"] || "",
          assetValue:                 row["Asset Value"] != null ? Number(row["Asset Value"]) : null,
          costType: row["CAPEX/OPEX"] || "",                  // NEW
          classification: row["Classification"] || "",        // NEW
          exposureFactor:             Number(row["Exposure Factor (%)"] || 50),
          annualizedRateOfOccurrence: Number(row["Annualized Rate of Occurrence"] || 0.2),
          orgId,
          currency:                   "USD",
          isSaved:                    true
        };
      });

      try {
        await axios.post(
         "https://oxu04tbck9.execute-api.ap-southeast-2.amazonaws.com/prod/api/assets/save",
          formattedAssets,
          { headers: { Region: region, "Content-Type": "application/json" } }
        );
        setAssets((prev) => [...prev, ...formattedAssets]);
        setToastMessage(`✅ ${formattedAssets.length} assets uploaded and saved!`);
        logActivity({
                  userData,
                  orgId:       orgId,
                  region:      sessionStorage.getItem('userRegion') || 'AP',
                  module:      'Asset Inventory',
                  category:    'assets',
                  action:      'Uploaded assets',
                  severity:    'success',
                  description: `${formattedAssets.length} assets with ${formattedAssets.assetName} are uploaded and saved`,});
        setToastType("success");
      } catch (err) {
        console.error("Upload save error:", err);
        const unsavedAssets = formattedAssets.map(a => ({ ...a, isSaved: false }));
        setAssets((prev) => [...prev, ...unsavedAssets]);
        setToastMessage(`⚠️ ${formattedAssets.length} assets loaded but failed to save to server.`);
        setToastType("error");
      }
      setShowToast(true);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  // ── Refresh ──
  const handleRefreshAssets = async () => {
    if (!orgId) return;
    setLoading(true);
    setErrorMessage("");
    try {
      const res = await axios.get(
       "https://hwul63huka.execute-api.ap-southeast-2.amazonaws.com/prod/api/assets/get-by-user",
        { params: { orgId }, headers: { Region: region } }
      );
      setAssets(res.data || []);
      setToastMessage("✅ Assets refreshed successfully!");
      setToastType("success");
      setShowToast(true);
    } catch {
      setToastMessage("❌ Failed to refresh assets.");
      setToastType("error");
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Pagination ──
  const filteredAssets = assets.filter(
    (a) =>
      a.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.assetSerialNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.assetCategory?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.owner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.costType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.classification?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(a.assetValue ?? "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pageCount = Math.ceil(filteredAssets.length / itemsPerPage);
  const paginatedAssets = filteredAssets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderPagination = () => (
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4">
      <div className="d-flex align-items-center mb-2 mb-md-0">
        <span className="me-2 text-muted">Rows per page:</span>
        <Form.Select
          value={itemsPerPage}
          onChange={(e) => { setCurrentPage(1); setItemsPerPage(parseInt(e.target.value)); }}
          className="w-auto"
          style={{ minWidth: "80px" }}
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </Form.Select>
      </div>
      <Pagination className="mb-0">
        <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1} />
        {[...Array(pageCount)].map((_, i) => (
          <Pagination.Item key={i} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)} className="rounded shadow-sm">
            {i + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pageCount))} disabled={currentPage === pageCount} />
        <Pagination.Last onClick={() => setCurrentPage(pageCount)} disabled={currentPage === pageCount} />
      </Pagination>
    </div>
  );

  // ── Delete ──
  const handleDeleteAsset = async () => {
    if (!assetToDelete?.assetId) {
      setToastMessage("❌ Asset ID is required for deletion.");
      setToastType("error");
      setShowToast(true);
      return;
    }

    try {
      setLoading(true);
      await axios.delete(
        "https://38a0q2k66e.execute-api.ap-southeast-2.amazonaws.com/prod/api/assets/delete",
        {
          params: { assetId: assetToDelete.assetId, orgId, assetRegion: assetToDelete.region },
          headers: { Region: region }
        }
      );
      setAssets(prevAssets => prevAssets.filter(asset => asset.assetId !== assetToDelete.assetId));
      setToastMessage(`Asset "${assetToDelete.assetName}" deleted successfully!`);
       logActivity({
                  userData,
                  orgId:       orgId,
                  region:      sessionStorage.getItem('userRegion') || 'AP',
                  module:      'Asset Inventory',
                  category:    'assets',
                  action:      'Asset Deleted',
                  severity:    'success',
                  description: `Asset "${assetToDelete.assetName}" is deleted `,});
      setToastType("success");
      setShowToast(true);
      setErrorMessage("");
      setShowDeleteModal(false);
      setAssetToDelete(null);
    } catch (error) {
      setToastMessage(`❌ Failed to delete asset: ${error.response?.data || error.message}`);
      setToastType("error");
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirmation = (asset) => {
    setAssetToDelete(asset);
    setShowDeleteModal(true);
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────

  return (
    <div>
      <Row className="g-0">
        <Col
          xs={12}
          className={`bg-transparent sidebar-container ${isSidebarCollapsed ? "collapsed" : "hello"}`}
          style={{ width: isSidebarCollapsed ? "90px" : "317px", transition: "width 0.3s ease" }}
        >
          <Menubar isCollapsed={isSidebarCollapsed} onToggle={handleSidebarToggle} />
        </Col>

        <Col
          className="pt-0 px-0 ps-0 section-container"
          style={{ width: `calc(100% - ${isSidebarCollapsed ? "90px" : "317px"})`, transition: "width 0.3s ease" }}
        >
          <NavigationBar userData={userData} isSidebarCollapsed={isSidebarCollapsed} />

          <Col className="overflow-auto p-4">
            <h5 className="mb-4 fw-bold">Asset / Inventory Management</h5>

            {/* ════════════════════════════════════════
                ADD NEW ASSET FORM
            ════════════════════════════════════════ */}
            <Card className="p-4 shadow-sm mb-4">
              <h5> Add a New Asset</h5>
              {showAlert && (
                <Alert variant="danger">Name, Location, and Owner are required.</Alert>
              )}

              {/* ── Section 1: Basic Asset Information ── */}
              <h6 className="mt-3 mb-3 text-primary">📋 Section 1: Basic Asset Information</h6>

              {/* Row: Asset ID (read-only) + Serial Number + Category */}
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <Form.Label className="fw-bold text-muted">Asset ID <small>(auto-generated)</small></Form.Label>
                  <Form.Control
                    value="Will be auto-generated (UUID) on save"
                    disabled
                    className="text-muted fst-italic bg-light"
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-bold">Asset Serial Number</Form.Label>
                  <Form.Control
                    placeholder="E.g. SN-2024-001"
                    name="assetSerialNumber"
                    value={newAsset.assetSerialNumber}
                    onChange={handleChange}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-bold">Asset Category</Form.Label>
                  <Form.Select
                    name="assetCategory"
                    value={newAsset.assetCategory}
                    onChange={handleChange}
                  >
                    <option value="">Select category...</option>
                    <option>Hardware</option>
                    <option>Software</option>
                    <option>Data / Information</option>
                    <option>Network</option>
                    <option>People</option>
                    <option>Physical</option>
                    <option>Cloud Service</option>
                    <option>Other</option>
                  </Form.Select>
                </Col>
              </Row>

              {/* Row: Asset Name + Description */}
              <Row>
                <Col><Form.Label className="fw-bold">Asset Name <span className="text-danger">*</span></Form.Label></Col>
                <Col><Form.Label className="fw-bold">Description</Form.Label></Col>
              </Row>
              <Row className="align-items-center g-3 mb-3">
                <Col>
                  <Form.Control
                    placeholder="E.g. Firewall, Server"
                    name="assetName"
                    value={newAsset.assetName}
                    onChange={handleChange}
                  />
                </Col>
                <Col>
                  <Form.Control
                    placeholder="Brief description"
                    name="description"
                    value={newAsset.description}
                    onChange={handleChange}
                  />
                </Col>
              </Row>

              {/* Row: Location + Owner + Asset Value */}
              <Row>
                <Col><Form.Label className="fw-bold">Location <span className="text-danger">*</span></Form.Label></Col>
                <Col><Form.Label className="fw-bold">Owner <span className="text-danger">*</span></Form.Label></Col>
                <Col><Form.Label className="fw-bold">Business Asset Value</Form.Label></Col>
                <Col md={2}><Form.Label className="fw-bold">CAPEX / OPEX</Form.Label></Col>
            <Col md={3}><Form.Label className="fw-bold">Classification</Form.Label></Col>
              </Row>
              <Row className="align-items-center g-3 mb-4">
                <Col>
                  <Form.Control
                    placeholder="E.g. London, New York"
                    name="location"
                    value={newAsset.location}
                    onChange={handleChange}
                  />
                </Col>
                <Col>
                  <Form.Control
                    placeholder="E.g. IT Manager"
                    name="owner"
                    value={newAsset.owner}
                    onChange={handleChange}
                  />
                </Col>
                <Col>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Amount (USD)"
                      name="assetValue"
                      value={newAsset.assetValue}
                      onChange={handleChange}
                    />
                  </InputGroup>
                </Col>
                <Col md={2}>
    <Form.Select
      name="costType"
      value={newAsset.costType}
      onChange={handleChange}
    >
      <option value="">Select...</option>
      <option value="CAPEX">CAPEX</option>
      <option value="OPEX">OPEX</option>
    </Form.Select>
  </Col>

  <Col md={3}>
    <Form.Select
      name="classification"
      value={newAsset.classification}
      onChange={handleChange}
    >
      <option value="">Select classification...</option>
      <option value="LOW">LOW</option>
      <option value="MEDIUM">MEDIUM</option>
      <option value="HIGH">HIGH</option>
      <option value="CRITICAL">CRITICAL</option>
    </Form.Select>
  </Col>
              </Row>

              {/* ── Section 2: Risk Factors ── */}
              <h6 className="mt-4 mb-3 text-danger">⚠️ Section 2: Risk Factors</h6>
              <p className="text-muted small mb-3">This ties directly to SLE/ALE calculations</p>

              <Row>
                <Col md={6}>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>% of value lost if asset compromised</Tooltip>}
                  >
                    <Form.Label className="fw-bold">
                      Exposure Factor (EF) - {newAsset.exposureFactor}%
                    </Form.Label>
                  </OverlayTrigger>
                  <Form.Range
                    min="0" max="100" step="1"
                    name="exposureFactor"
                    value={newAsset.exposureFactor}
                    onChange={handleChange}
                    className="mb-2"
                  />
                  <div className="d-flex justify-content-between text-muted small">
                    <span>0%</span><span>50%</span><span>100%</span>
                  </div>
                </Col>
                <Col md={6}>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Expected frequency per year (0.2 = once in 5 years)</Tooltip>}
                  >
                    <Form.Label className="fw-bold">
                      Annualized Rate of Occurrence (ARO)
                    </Form.Label>
                  </OverlayTrigger>
                  <Form.Control
                    type="number"
                    min="0" max="10" step="0.1"
                    placeholder="0.2"
                    name="annualizedRateOfOccurrence"
                    value={newAsset.annualizedRateOfOccurrence}
                    onChange={handleChange}
                  />
                  <Form.Text className="text-muted">Default: 0.2 (once every 5 years)</Form.Text>
                </Col>
              </Row>

             <div className="d-flex gap-2 mt-3">
<button
  type="button"
  className="primary1 btn btn-primary mt-3"
  onClick={handleSaveAsset}
>
  ➕ Add Asset
</button>

  {isEditMode && (
    <button
      type="button"
      className="btn btn-outline-secondary"
      onClick={resetForm}
    >
      Cancel Edit
    </button>
  )}
</div>
            </Card>

            {/* ════════════════════════════════════════
                UPLOAD SPREADSHEET
            ════════════════════════════════════════ */}
            <Card className="p-4 shadow-sm mb-4">
              <h5> Upload Spreadsheet</h5>
             <p className="text-muted mb-2">
             Format: <strong>Asset ID, Asset Serial Number, Asset Category, Name of the Asset, Description, Location, Owner, Asset Value, CAPEX/OPEX, Classification, Exposure Factor (%), Annualized Rate of Occurrence</strong>
             </p>
              <Button className="mb-3 w-25" onClick={handleDownloadSample}>
                 Download Sample Template
              </Button>
              <Form.Control type="file" accept=".xlsx, .xls" onChange={handleUpload} />
            </Card>

            {/* ════════════════════════════════════════
                INVENTORY TABLE
            ════════════════════════════════════════ */}
            <Card className="p-4 shadow-sm">
              <Row className="mb-3 align-items-center">
                <Col md={4} className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">Inventory List</h5>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Refresh asset list</Tooltip>}>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleRefreshAssets}
                      disabled={loading}
                      style={{ minWidth: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : "🔄"}
                    </Button>
                  </OverlayTrigger>
                </Col>
                <Col md={8}>
                  <InputGroup>
                    <Form.Control
                      placeholder="Search by name, serial number, category, location or owner..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>

              {loading ? (
                <Spinner animation="border" variant="primary" />
              ) : (
               <>
  <div className="inventory-table-wrap refined-table-wrap">
    <Table hover responsive className="asset-table refined-asset-table align-middle mb-0">
     <thead>
  <tr>
    <th style={{ width: "60px" }}>#</th>
    <th style={{ minWidth: "280px" }}>Asset</th>
    <th style={{ minWidth: "280px" }}>Description</th>
    <th style={{ minWidth: "120px" }}>Category</th>
    <th style={{ minWidth: "120px" }}>Location</th>
    <th style={{ minWidth: "120px" }}>Owner</th>
    <th style={{ minWidth: "140px" }}>Value</th>
    <th style={{ minWidth: "110px" }}>Type</th>
    <th style={{ minWidth: "140px" }}>Classification</th>
    <th style={{ minWidth: "110px" }}>Risk</th>
    <th style={{ minWidth: "120px" }} className="text-center">Actions</th>
  </tr>
</thead>

      <tbody>
  {paginatedAssets.map((asset, index) => (
    <tr key={asset.assetId || index}>
      <td className="index-cell">
        {(currentPage - 1) * itemsPerPage + index + 1}
      </td>

      <td>
        <div className="asset-main-cell refined-main-cell">
          <div className="asset-title-row">
            <span className="asset-name">{asset.assetName || "Untitled Asset"}</span>
          </div>

          <div className="asset-meta-block">
            <div className="asset-meta-item">
              <span className="asset-meta-label">Serial Number:</span>
              <span className="asset-meta-value">
                {asset.assetSerialNumber || "—"}
              </span>
            </div>

            <div className="asset-meta-item asset-id-row">
              <span className="asset-meta-label">Asset ID:</span>
              <span className="asset-id-chip">
                {asset.assetId || "—"}
              </span>
            </div>
          </div>
        </div>
      </td>

      <td>
        <div className="description-cell">
          {asset.description || "—"}
        </div>
      </td>

      <td>
        <span className="soft-badge neutral">
          {asset.assetCategory || "—"}
        </span>
      </td>

      <td>{asset.location || "—"}</td>

      <td>{asset.owner || "—"}</td>

      <td className="value-cell">
        {formatCurrency(asset.assetValue) || "—"}
      </td>

      <td>
        <span className="soft-badge type-pill">
          {asset.costType || "—"}
        </span>
      </td>

      <td>
        <span
          className={`soft-badge classification ${
            asset.classification ? asset.classification.toLowerCase() : ""
          }`}
        >
          {asset.classification || "—"}
        </span>
      </td>

      <td>
        <div className="risk-cell refined-risk-cell">
          <span className="risk-ef">{asset.exposureFactor || 50}% EF</span>
          <small className="risk-aro">
            {asset.annualizedRateOfOccurrence || 0.2} ARO
          </small>
        </div>
      </td>

      <td className="text-center actions-cell">
        <div className="action-stack-v2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEditAsset(asset)}
            disabled={loading}
          >
            Update
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleDuplicateAsset(asset)}
            disabled={loading}
          >
            Duplicate
          </Button>

          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => showDeleteConfirmation(asset)}
            disabled={loading}
          >
            Delete
          </Button>
        </div>
      </td>
    </tr>
  ))}
</tbody>
    </Table>
  </div>

  {renderPagination()}
</>
              )}

              {successMessage && (
                <Alert className="mt-3" variant="info">{successMessage}</Alert>
              )}
              {errorMessage && (
                <Alert className="mt-3" variant="danger">{errorMessage}</Alert>
              )}
            </Card>
          </Col>

          <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />
        </Col>
      </Row>

      {/* ── Delete Confirmation Modal ── */}
      <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setAssetToDelete(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the asset:</p>
          <strong>"{assetToDelete?.assetName}"</strong>
          {assetToDelete?.assetSerialNumber && (
            <p className="text-muted mt-1 mb-0">
              <small>Serial Number: {assetToDelete.assetSerialNumber}</small>
            </p>
          )}
          <p className="assetAction text-danger ">⚠️ This action cannot be undone!</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => { setShowDeleteModal(false); setAssetToDelete(null); }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteAsset} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
  show={showEditModal}
  onHide={() => setShowEditModal(false)}
  size="lg"
  backdrop="static"
>
  <Modal.Header closeButton>
    <Modal.Title>✏️ Update Asset</Modal.Title>
  </Modal.Header>

  <Modal.Body>
    <Row className="g-3">
      <Col md={6}>
        <Form.Label className="fw-bold">Asset Serial Number</Form.Label>
        <Form.Control
          name="assetSerialNumber"
          value={editAsset.assetSerialNumber}
          onChange={handleEditChange}
        />
      </Col>

      <Col md={6}>
        <Form.Label className="fw-bold">Asset Category</Form.Label>
        <Form.Select
          name="assetCategory"
          value={editAsset.assetCategory}
          onChange={handleEditChange}
        >
          <option value="">Select category...</option>
          <option>Hardware</option>
          <option>Software</option>
          <option>Data / Information</option>
          <option>Network</option>
          <option>People</option>
          <option>Physical</option>
          <option>Cloud Service</option>
          <option>Other</option>
        </Form.Select>
      </Col>

      <Col md={6}>
        <Form.Label className="fw-bold">Asset Name</Form.Label>
        <Form.Control
          name="assetName"
          value={editAsset.assetName}
          onChange={handleEditChange}
        />
      </Col>

      <Col md={6}>
        <Form.Label className="fw-bold">Owner</Form.Label>
        <Form.Control
          name="owner"
          value={editAsset.owner}
          onChange={handleEditChange}
        />
      </Col>

      <Col md={12}>
        <Form.Label className="fw-bold">Description</Form.Label>
        <Form.Control
          name="description"
          value={editAsset.description}
          onChange={handleEditChange}
        />
      </Col>

      <Col md={6}>
        <Form.Label className="fw-bold">Location</Form.Label>
        <Form.Control
          name="location"
          value={editAsset.location}
          onChange={handleEditChange}
        />
      </Col>

      <Col md={3}>
        <Form.Label className="fw-bold">Asset Value</Form.Label>
        <InputGroup>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            name="assetValue"
            value={editAsset.assetValue}
            onChange={handleEditChange}
          />
        </InputGroup>
      </Col>

      <Col md={3}>
        <Form.Label className="fw-bold">CAPEX / OPEX</Form.Label>
        <Form.Select
          name="costType"
          value={editAsset.costType}
          onChange={handleEditChange}
        >
          <option value="">Select...</option>
          <option value="CAPEX">CAPEX</option>
          <option value="OPEX">OPEX</option>
        </Form.Select>
      </Col>

      <Col md={4}>
        <Form.Label className="fw-bold">Classification</Form.Label>
        <Form.Select
          name="classification"
          value={editAsset.classification}
          onChange={handleEditChange}
        >
          <option value="">Select classification...</option>
          <option value="LOW">LOW</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="HIGH">HIGH</option>
          <option value="CRITICAL">CRITICAL</option>
        </Form.Select>
      </Col>

      <Col md={4}>
        <Form.Label className="fw-bold">Exposure Factor (%)</Form.Label>
        <Form.Control
          type="number"
          min="0"
          max="100"
          step="1"
          name="exposureFactor"
          value={editAsset.exposureFactor}
          onChange={handleEditChange}
        />
      </Col>

      <Col md={4}>
        <Form.Label className="fw-bold">ARO</Form.Label>
        <Form.Control
          type="number"
          min="0"
          max="10"
          step="0.1"
          name="annualizedRateOfOccurrence"
          value={editAsset.annualizedRateOfOccurrence}
          onChange={handleEditChange}
        />
      </Col>
    </Row>
  </Modal.Body>

  <Modal.Footer>
    <Button
      variant="secondary"
      onClick={() => setShowEditModal(false)}
      disabled={loading}
    >
      Cancel
    </Button>

    <Button
      variant="primary"
      onClick={handleUpdateAsset}
      disabled={loading}
    >
      {loading ? <Spinner animation="border" size="sm" /> : "Update Asset"}
    </Button>
  </Modal.Footer>
</Modal>

      {/* ── Toast Notifications ── */}
      <ToastContainer
        position="top-end"
        className="position-fixed"
        style={{ zIndex: 9999, top: '20px', right: '20px' }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={4000}
          autohide
          bg={toastType === "success" ? "success" : "danger"}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastType === "success" ? "🎉 Success" : "❌ Error"}
            </strong>
            <small>Just now</small>
          </Toast.Header>
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default AssetInventory;
